import { useState } from "react";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db6c3hnqhq9pk5mecsc0";

type Status = "idle" | "submitting" | "success" | "error";

export default function Contact() {
  usePageMeta(
    "联系我们 - 伦理片在线观看",
    "通过伦理片在线观看联系页面与我们取得联系，提交反馈、内容纠错或版权相关事宜，我们会尽快回复您。",
  );

  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      return;
    }

    const name = String(formData.get("name") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();

    if (!name || !email || !message) {
      setStatus("error");
      setFormError("请填写完整的姓名、邮箱与留言内容。");
      return;
    }
    if (message.length > 500) {
      setStatus("error");
      setFormError("留言内容不能超过 500 个字符。");
      return;
    }

    const payload = new URLSearchParams();
    payload.append("name", name);
    payload.append("email", email);
    payload.append("message", message);

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null =
        null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后再试。");
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "联系我们" }]} />

      <header className="mt-5">
        <h1 className="font-heading text-3xl font-bold text-foreground-950">联系我们</h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">
          如果你对平台内容有任何建议、发现信息有误，或需要咨询版权相关事宜，欢迎通过下方表单与我们联系，我们会尽快回复。
        </p>
      </header>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-5">
        <div className="flex flex-col gap-4 lg:col-span-2">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-50 text-primary-600">
              <i className="ri-mail-line text-xl"></i>
            </span>
            <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">邮件联系</h3>
            <p className="mt-2 text-sm text-foreground-600">service@ethicsfilm.example</p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-50 text-accent-600">
              <i className="ri-time-line text-xl"></i>
            </span>
            <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">服务时间</h3>
            <p className="mt-2 text-sm text-foreground-600">周一至周五 09:00 - 18:00</p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
              <i className="ri-map-pin-line text-xl"></i>
            </span>
            <h3 className="mt-4 font-heading text-base font-semibold text-foreground-950">办公地址</h3>
            <p className="mt-2 text-sm text-foreground-600">中国 · 上海市静安区文化创意产业园</p>
          </div>
          <p className="flex items-start gap-2 rounded-lg bg-background-100 p-4 text-xs leading-relaxed text-secondary-500">
            <i className="ri-information-line mt-0.5"></i>
            本站不提供影音存储与下载服务，如需反馈作品资料问题，请在留言中注明片名与具体内容。
          </p>
        </div>

        <div className="lg:col-span-3">
          <form
            id="contact-form"
            data-readdy-form
            action={SUBMIT_ADDR}
            method="post"
            onSubmit={handleSubmit}
            className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
          >
            <h2 className="font-heading text-xl font-bold text-foreground-950">在线留言</h2>
            <p className="mt-2 text-sm text-foreground-600">带 * 的为必填项</p>

            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground-800">
                  姓名 <span className="text-accent-600">*</span>
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  placeholder="请输入您的姓名"
                  className="mt-2 w-full rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-secondary-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground-800">
                  邮箱 <span className="text-accent-600">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="请输入您的邮箱"
                  className="mt-2 w-full rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-secondary-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
            </div>

            <div className="mt-5">
              <label htmlFor="message" className="block text-sm font-medium text-foreground-800">
                留言内容 <span className="text-accent-600">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="请输入您的建议或反馈（最多 500 字）"
                className="mt-2 w-full resize-none rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-secondary-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              ></textarea>
              <p className="mt-1.5 text-xs text-secondary-400">最多 500 个字符</p>
            </div>

            <input
              type="text"
              name="website_alt"
              className="form-alt-field"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <div className="mt-6 flex flex-wrap items-center gap-4">
              <button
                type="submit"
                disabled={status === "submitting"}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
                {status === "submitting" ? "提交中..." : "提交留言"}
              </button>
              {status === "success" && (
                <span className="flex items-center gap-1.5 text-sm text-primary-700">
                  <i className="ri-checkbox-circle-line"></i>
                  提交成功，感谢您的留言！
                </span>
              )}
            </div>

            {status === "error" && formError && (
              <p className="mt-4 flex items-start gap-2 rounded-md bg-accent-50 px-3 py-2.5 text-sm text-accent-700">
                <i className="ri-error-warning-line mt-0.5"></i>
                {formError}
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}